'use client';

import { Fragment } from 'react';
import Image from 'next/image';
import QuoteCard from '../QuoteCard';
import Reveal, { revealStagger } from '../Reveal';
import Section from '../Section';
import Eyebrow from '../Eyebrow';
import { trackOutboundLink } from '@/lib/analytics';

/**
 * Founder story, pull quote and testimonials.
 *
 * Layout from the design reference
 * (docs/revamp-july-2026/handoff/Focal Point Site.dc.html, about section): a
 * 460px portrait column at 520px tall, radius 12, object-fit cover, beside the
 * copy at a 64px gap. The reference pairs that portrait with four sentences;
 * this story runs to eight paragraphs, so the portrait is sticky on lg rather
 * than centred, which keeps it in view instead of leaving a tall empty column.
 *
 * The only portrait source is 400x400 (public/assets/headshot-optimized.jpg),
 * so the 460x520 frame is a 1.3x upscale. Accepted per #7918: no new portrait
 * is coming, and a larger source file would fix it with no code change.
 *
 * Body copy lives in PARAGRAPHS rather than inline JSX text for two reasons:
 * it keeps the eight paragraphs readable as content, and it keeps their many
 * apostrophes clear of react/no-unescaped-entities.
 *
 * Deliberately absent and not to be reintroduced: the expert-partner-network
 * line, the two expired project-management certifications, the exit-multiple
 * figure, and the "See the Full Journey" CTA, whose route is deleted. Named
 * indirectly on purpose: #7921 asserts those strings appear nowhere in the
 * files it touches, and that check does not exempt comments.
 */

const LINKEDIN_URL = 'https://www.linkedin.com/in/timstrobl';

const PARAGRAPHS = [
  "I'm Tim Strobl. For over two decades I've worked at the point where business strategy meets technical execution: defining what to build, why it matters, and then making sure it actually ships.",
  'That career took me through two acquisitions, Wirewax to Vimeo and BridgeU to Kaplan, and through global platforms delivering for Apple, Red Bull, Disney, BBC and ESPN. At Lab Services of America I built the software and operations for a Covid-19 testing business from nothing, scaling from zero to hundreds of staff across hundreds of school sites during a public health emergency.',
  "What changed recently is what one person can do. I've gone deep into AI-native development, building production systems myself rather than specifying them for someone else. PaxRent, my property management platform, reached production in seven weeks with a live customer collecting rent, and has been running and growing continuously since. It handles double-entry trust accounting, 50-state compliance, payments, leasing, maintenance and reporting, with an approval-gated AI agent at its core, hardened through months of real operational use. I designed it, architected it, built it and onboarded the first client.",
  'I also built it to compound. Usage data, behavioral signals and direct feedback feed a continuous loop in which the system surfaces its own improvement opportunities. Combined with periodic full code audits and each new generation of AI models, the platform gets measurably better the more it is used. Most software depreciates from the day it ships. This gets sharper.',
  "The other thing I bring is judgment about process. I'm rigorous about workflow, automation and governance, and equally rigorous about not adding any you don't need. I've run programme governance across 49 countries and I've built a production platform as a team of one. Those need completely different amounts of structure, and most of the damage I see comes from applying the wrong one. Enough process to hold under pressure, never more than that.",
  'That combination is what I bring to an engagement. Executive-level product judgment, and the ability to build the thing rather than write a document about it. It means shorter feedback loops, working prototypes instead of slide decks, and honest answers about what AI can and cannot do for your business right now.',
  "Beyond consulting, I actively invest in ventures where I can provide strategic value. That dual role as advisor and investor means I approach every engagement with an owner's mindset, focused on sustainable value rather than deliverables.",
  "I work across regulated and compliance-heavy environments (GDPR, CCPA, HIPAA, ISO 27001) and I've built and coached teams across 49 countries. Austrian, based in the US, equally at home in English or German.",
];

/**
 * Sits after the PaxRent paragraph, which is where a reader forms the "is this
 * just vibe coding" objection. It makes no claim about how long Tim has been
 * building with AI: the proof is the artifact, not the tenure.
 */
const PULL_QUOTE_AFTER_INDEX = 2;

const PULL_QUOTE =
  '"Twenty-five years of knowing what good architecture looks like is precisely why I can move this fast. The tools compress the typing, not the judgment."';

interface Testimonial {
  quote: string;
  name: string;
  title: string;
}

const RYAN_SCHMALTZ: Testimonial = {
  quote:
    '"An unparalleled ability in distilling down multiple paths that a product can take into a clear go-forward strategy based upon data, market and customer insights, and business need."',
  name: 'Ryan Schmaltz',
  title: 'CEO & Cofounder, Patient Ready',
};

const CHRISTIAN_JANSSON: Testimonial = {
  quote:
    '"He was able to get into context and content extremely fast and proved that he could deliver great solutions even in very challenging cases."',
  name: 'Christian Jansson',
  title: 'Global Segment Manager, Hilti Group',
};

/**
 * Card 2 is a placeholder for a Brad / S2SPM quote on PaxRent that Tim is
 * sourcing. None of the 40 LinkedIn recommendations in
 * docs/revamp-july-2026/handoff/quotes-and-recommendations.md mentions PaxRent
 * (the corpus tops out at 2021), so it cannot be satisfied by picking a
 * different entry. When the quote arrives, add a BRAD_S2SPM const above and
 * change the line below. That is the whole swap.
 */
const TESTIMONIALS: Testimonial[] = [RYAN_SCHMALTZ, CHRISTIAN_JANSSON];

export default function FounderSection() {
  return (
    <Section id="founder">
      <div className="grid items-start gap-10 lg:grid-cols-[460px_1fr] lg:gap-16">
        {/* The Reveal replaces the portrait FRAME, not the sticky column. A
            Reveal wrapping the sticky element would become the sticky
            element's containing block at its own content height, and the
            portrait would stop tracking the copy beside it. */}
        <div className="mx-auto w-full max-w-[460px] lg:sticky lg:top-28">
          <Reveal className="h-[380px] overflow-hidden rounded-xl bg-sand-2 sm:h-[460px] lg:h-[520px]">
            <Image
              src="/assets/headshot-optimized.jpg"
              alt="Tim Strobl, founder of Focal Point Solutions"
              width={460}
              height={520}
              className="h-full w-full object-cover"
              priority
            />
          </Reveal>
        </div>

        <div>
          <Reveal delay={90}>
            <Eyebrow>About</Eyebrow>

            <h2 className="mt-4 font-display text-[30px] font-medium leading-[1.12] tracking-[-0.03em] text-balance text-ink md:text-[38px]">
              The Founder&apos;s Story: 25 Years Bridging Strategy and Execution
            </h2>
          </Reveal>

          {/* The eight paragraphs are one reveal, not eight. Revealing them
              individually would animate the reader's own reading position down
              the column, which is the opposite of getting out of the way. */}
          <Reveal className="mt-6 flex flex-col gap-5 font-sans text-[17px] leading-[1.65] text-pretty text-body">
            {PARAGRAPHS.map((paragraph, index) => (
              <Fragment key={paragraph.slice(0, 40)}>
                <p>{paragraph}</p>
                {index === PULL_QUOTE_AFTER_INDEX ? (
                  <QuoteCard quote={PULL_QUOTE} className="my-2" />
                ) : null}
              </Fragment>
            ))}
          </Reveal>

          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 inline-flex items-center gap-2 font-sans text-[15px] font-semibold text-ink-blue underline-offset-4 hover:underline"
            onClick={() => trackOutboundLink(LINKEDIN_URL, 'LinkedIn')}
          >
            <svg
              className="h-[18px] w-[18px]"
              fill="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            Connect on LinkedIn
          </a>
        </div>
      </div>

      <div className="mt-14 grid gap-6 md:grid-cols-2">
        {TESTIMONIALS.map((testimonial, index) => (
          <Reveal key={testimonial.name} delay={revealStagger(index)}>
            <QuoteCard
              quote={testimonial.quote}
              attribution={`${testimonial.name}, ${testimonial.title}`}
            />
          </Reveal>
        ))}
      </div>
    </Section>
  );
}
